import { useState, useEffect, useRef } from 'react';
import { NoRecords, PageLoader } from '../../../lib/loader';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useGetRewardFailed, useUpdateReward } from '../api/apiReferral';
import { Copy, Check, ExternalLink, Search, SquarePen, ArrowUp, ArrowDown, X } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { copyToClipboard } from '../../../lib/copytoClipboard';
import { useModalContext } from '../../../contexts/ModalContext';
import { useToast } from '../../../hooks/usetoast';
import { useCheckAdminAccess } from '../../WhiteIp/api/whiteipapi';
import { logout } from '../../../hooks/logout';
import PermissionDenied from '../../common/permissionDenied';
import { WaitingLoader } from '../../../hooks/buttonLoad';
import { transactionurl } from "../../../lib/transactionurl";
import { useBreadcrumbPagination } from '../../../hooks/useBreadcrumbPagination';
import BreadcrumbPagination from '../../../utils/BreadcrumbPagination';
import { useDebounce } from '../../../hooks/debounce';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { queryClient } from '../../../config';
import moment from 'moment';
import { displayValue } from '../../../hooks/export';



function ReferralPending() {

    const [tempPage, setTempPage] = useState(0);
    const [tempRows, setTempRows] = useState(25);
    const [double, setDouble] = useState(false);
    const [search, setSearch] = useState("");
    const [openModal, setOpenModal] = useState(false);
    const [selectedReward, setSelectedReward] = useState(null);

    const searchText = useDebounce(search);
    const { theme } = useModalContext()
    const toast = useToast();
    const [sortOrder, setSortOrder] = useState("desc");
    const { id } = useParams();
    const [userEmail, setUserEmail] = useState(false);
    const [referralEmail, setReferralEmail] = useState(false);
    const updateRewardMutation = useUpdateReward();

    const { data: getRewardFailed, isLoading } = useGetRewardFailed({
        id,
        page: searchText ? 0 : tempPage,
        size: tempRows,
        searchText,
        sortField: "createdAt",
        sortOrder
    });

    const totalRecords = getRewardFailed?.data?.totalRecords || 0;

    const {
        page,
        rowsPerPage,
        totalPages,
        handleChangePage,
        handleChangeRowsPerPage
    } = useBreadcrumbPagination(totalRecords, tempRows);

    useEffect(() => {
        setTempPage(page);
    }, [page]);

    useEffect(() => {
        setTempRows(rowsPerPage);
    }, [rowsPerPage]);

    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            const length = inputRef.current.value.length;
            inputRef.current.setSelectionRange(length, length);
        }
    });

    useEffect(() => {
        setTempPage(0);
        if (handleChangePage) {
            handleChangePage(null, 0);
        }
    }, [searchText]);


    const copyUserEmailAddress = async data => {
        setUserEmail(data._id)
        await copyToClipboard(data?.userId?.email)
        toast.success("Copied")
        setTimeout(() => {
            setUserEmail()
        }, 800)
    }

    const copyReferralEmailAddress = async data => {
        setReferralEmail(data._id)
        await copyToClipboard(data?.referUserId?.email)
        toast.success("Copied")
        setTimeout(() => {
            setReferralEmail()
        }, 800)
    }

    const checkAdminMutation = useCheckAdminAccess();
    const [access, setAccess] = useState(true);

    useEffect(() => {
        checkAdminAccess();

    }, []);

    const renderCopyIcon = (isCopied, onClick, theme) => (
        isCopied ?
            <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : "black" }} />
            :
            <Copy onClick={onClick} size={20} className="ml-5" style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : "black" }} />
    );

    const checkAdminAccess = async () => {
        try {
            const obj = { page: 'Referral Management' };
            const checkAdminRes = await checkAdminMutation.mutateAsync(obj);
            if (!checkAdminRes?.data?.status) {
                setAccess(false);
                logout();
            } else {
                setAccess(true);
            }
        } catch (error) {
            setAccess(false);
        }
    };


    const copytxnhashAddress = async data => {
        setDouble(data._id)
        await copyToClipboard(data)
        toast.success("Copied")
        setTimeout(() => {
            setDouble()
        }, 800)
    }


    const renderTransactionLink = (hash, urlPrefix, isCopied, onClickCopy, theme) => (
        hash ? (
            <>
                {`${hash.slice(0, 4)}...${hash.slice(-4)}`}
                {renderCopyIcon(isCopied, onClickCopy, theme)}
                <a href={urlPrefix + hash} target="_blank" rel="noopener noreferrer" className="ml-2">
                    <ExternalLink style={{ marginLeft: "5px" }} size={20} />
                </a>
            </>
        ) : "-"
    );

    const handleRewardAction = (row) => {
        setSelectedReward(row);
        setOpenModal(true);
    };

    const updateRewardStatus = async (status) => {
        try {
            const response = await updateRewardMutation.mutateAsync({
                _id: selectedReward._id,
                refSatus: status
            })

            if (!response?.status) {
                toast.error(response?.data?.message);

            } else {
                toast.success(response?.data?.message)
                queryClient.refetchQueries('Referral-failed')
            }
        } catch (error) {
            console.error("Error updating reward:", error);
        } finally {
            setOpenModal(false);
        }
    };



    const renderTotalReferralhistoryTable = () => (
        getRewardFailed?.data?.usersHistory?.length > 0 ? (
            <>
                <div style={{ minHeight: "60vh" }}>
                    <div className='table-responsive'>
                        <Table sx={{ minWidth: 650 }} aria-label="Referral transaction history table">
                            <TableHead>
                                <TableRow>
                                    <TableCell className='text-nowrap' align="center">S.NO</TableCell>
                                    <TableCell className='text-nowrap' align="center">Reward Type</TableCell>
                                    <TableCell className='text-nowrap' align="center">Referrer Email</TableCell>
                                    <TableCell className='text-nowrap' align="center">User Email</TableCell>
                                    <TableCell className='text-nowrap' align="center">From User Hash</TableCell>
                                    <TableCell className='text-nowrap' align="center">To User Hash</TableCell>
                                    <TableCell className='text-nowrap' align="center">Reward Amount</TableCell>
                                    <TableCell className='text-nowrap' align="center">Reward Status</TableCell>
                                    <TableCell className='text-nowrap'>Date and Time
                                        {sortOrder !== "desc" ? <ArrowUp onClick={() => setSortOrder(' desc')} style={{ cursor: "pointer" }} />
                                            : <ArrowDown onClick={() => setSortOrder('asc')} style={{ cursor: "pointer" }} />}</TableCell>
                                    <TableCell className='text-nowrap' align="center">Action</TableCell>

                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getRewardFailed?.data?.usersHistory?.map((row, i) => (
                                    <TableRow key={row?._id || i}>
                                        <TableCell align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                                        <TableCell>{row.rewardType == 'Type-I' ? 'Referral Reward' : 'Cashback Reward'}</TableCell>
                                        <TableCell align="center">
                                            {row?.userId?.email || "admin@roupay.com"}
                                            {userEmail === row._id ? (
                                                <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : 'black' }} />
                                            ) : (
                                                <Copy
                                                    onClick={() => copyUserEmailAddress(row)}
                                                    size={20}
                                                    className='ml-5'
                                                    style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : 'black' }}
                                                />
                                            )}
                                        </TableCell>

                                        <TableCell align="center">
                                            {row?.referUserId?.email || "-"}
                                            {referralEmail === row._id ? (
                                                <Check size={20} style={{ marginLeft: "5px", color: theme ? "white" : 'black' }} />
                                            ) : (
                                                <Copy
                                                    onClick={() => copyReferralEmailAddress(row)}
                                                    size={20}
                                                    className='ml-5'
                                                    style={{ cursor: "pointer", marginLeft: "5px", color: theme ? "white" : 'black' }}
                                                />
                                            )}
                                        </TableCell>

                                        <TableCell align="center">
                                            {renderTransactionLink(row.fromTxnHash, transactionurl["POL"], double === row._id, () => copytxnhashAddress(row.fromTxnHash), theme)}
                                        </TableCell>


                                        <TableCell align="center">
                                            {renderTransactionLink(row.toTxnHash, transactionurl["POL"], double === row._id, () => copytxnhashAddress(row.toTxnHash), theme)}
                                        </TableCell>

                                        <TableCell className="text-nowrap" align="center">
                                            {row?.rewardAmount ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                                    {displayValue(row.rewardAmount, 6)}

                                                    {row?.currencyImage && (
                                                        <img
                                                            src={row.currencyImage}
                                                            alt="Currency Icon"
                                                            style={{
                                                                height: '20px',
                                                                width: '20px',
                                                                borderRadius: '50%',
                                                                verticalAlign: 'middle',
                                                                objectFit: 'cover',
                                                            }}
                                                        />
                                                    )}
                                                </span>
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        {
                                            row?.rewardStatus == "Pending" ?
                                                <TableCell align="center" className=' text-warning'>{row?.rewardStatus || "-"}</TableCell>
                                                :
                                                <TableCell align="center" className=' text-danger'>{row?.rewardStatus || "-"}</TableCell>


                                        }
                                        <TableCell className='text-nowrap'>
                                            {moment(row?.createdAt).format("DD-MM-YYYY | hh:mm A")}
                                        </TableCell>
                                        {row?.rewardStatus == "Pending" ?
                                            <TableCell align="center">
                                                <SquarePen className="mx-3" size={20} type='button' onClick={() => handleRewardAction(row)}
                                                />
                                            </TableCell>
                                            :
                                            <TableCell align="center">
                                                <span>---</span>
                                            </TableCell>
                                        }

                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
                <BreadcrumbPagination
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalPages={totalPages}
                    totalRecords={totalRecords}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
                <Dialog open={openModal} onClose={() => setOpenModal(false)}>
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Update Reward Status
                        <IconButton
                            aria-label="close"
                            onClick={() => setOpenModal(false)}
                            sx={{ color: 'grey.500' }}
                        >
                            <X />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <p>Do you want to approve or reject this reward for <strong>{selectedReward?.userId?.email}</strong>?</p>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => updateRewardStatus("Rejected")} color="error">Reject</Button>
                        <Button onClick={() => updateRewardStatus("Approve")} color="primary">Approve</Button>
                    </DialogActions>
                </Dialog>

            </>
        ) : (
            <NoRecords />
        )
    );

    let content;

    if (isLoading) {
        content = <WaitingLoader />;
    } else if (access) {
        content = (
            <section>
                <h4>Referral History</h4>
                <div className="ad_pg_bx bg-white p-3">
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                        <div className="col-md-4">
                            <div className="input-group">
                                <span className="input-group-icon">
                                    <Search size={20} color="#aaa9a9" />
                                </span>
                                <input
                                    type="text"
                                    ref={inputRef}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="form-control"
                                    placeholder="Search Email"
                                    value={search}
                                />
                            </div>
                        </div>
                    </div>
                    {isLoading ? <PageLoader /> : renderTotalReferralhistoryTable()}
                </div>
            </section>
        );
    } else {
        content = <PermissionDenied />;
    }

    return <>{content}</>;

}

export default ReferralPending;